import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { PermissionEnum } from '@ui/src';
import { useAppDispatch } from '@store';
import {
  AffilatedCompany,
  AffilatedCompanyFormValues,
  AllowedCategory,
  CRSAndFatca,
  CRSAndFatcaFormValues,
  DictionaryItem,
  NewApplicationListItem,
} from '../../types';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import {
  useCreateAffilatedCompaniesMutation,
  useCreateCOFCRSAndFactaMutation,
  useDeleteAffilatedCompaniesMutation,
  useDeleteCOFCRSAndFactaMutation,
  useLazyGetAffilatedCompaniesQuery,
  useLazyGetApplicationQuestionnarieQuery,
  useLazyGetCOFCRSAndFactaQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetSourceOfIncomeDictionaryQuery,
  useLazyGetSourceOfWealthForIndividualDictionaryQuery,
  useUpdateAffilatedCompaniesMutation,
  useUpdateCOFCRSAndFactaMutation,
} from '../../store/services';
import { setAllowedDocumentsCategories } from '../store/slices';

const useQuestionnarie = ({
  affilatedCompanies,
  crsAndFatca,
  questionnarieId,
  newApplicationInfo,
  questionnarie,
  sourceOfWealth,
  activePermissions,
  sourceOfIncome,
  sourceOfWealthForIndividual,
}: {
  affilatedCompanies?: AffilatedCompany[];
  crsAndFatca?: CRSAndFatca[];
  questionnarieId?: string;
  newApplicationInfo?: NewApplicationListItem;
  questionnarie?: COFAndIndividualQuestionnarieFormValues;
  activePermissions?: PermissionEnum[];
  sourceOfWealth?: DictionaryItem[];
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
}) => {
  const [defaultQuestionnarieFormValues, setDefaultQuestionnarieFormValues] =
    useState({});
  const [haveSecondPassport, setHaveSecondPassport] = useState(false);
  const [crsAndFatcaValues, setCrsAndFatcaValues] = useState<
    CRSAndFatcaFormValues[]
  >([
    {
      id: 'crs-and-fatca-1',
      residenceCountryId: '',
      taxIdentificationNumber: '',
      noTinReason: '',
    },
  ]);
  const [selectedCrsAndFatcaId, setSelectedCrsAndFatcaId] = useState('');
  const [affilatedCompaniesValues, setAffilatedCompaniesValues] = useState<
    AffilatedCompanyFormValues[]
  >([
    {
      id: 'affilated-company-1',
      name: '',
      registrationNumber: '',
      mainActivities: '',
      position: '',
      countryId: '',
      percentageOfOwnership: '',
    },
  ]);
  const [selectedAffilatedCompanyId, setSelectedAffilatedCompanyId] =
    useState('');

  const dispatch = useAppDispatch();

  const {
    control: questionnarieControl,
    reset: questionnarieReset,
    clearErrors: questionnarieClearErrors,
    formState: { errors: questionnarieErrors, isDirty: questionnarieIsDirty },
    watch: questionnarieWatch,
    getValues: questionnarieGetValues,
    setError: questionnarieSetError,
    setValue: questionnarieSetValue,
  } = useForm<COFAndIndividualQuestionnarieFormValues>();

  const questionnarieFields = questionnarieWatch();

  const [
    getApplicationQuestionnarie,
    { isLoading: questionnarieIsLoading, isFetching: questionnarieIsFetching },
  ] = useLazyGetApplicationQuestionnarieQuery();
  const [getCustomerSourceOfWealth] = useLazyGetCustomerSourceOfWealthQuery();
  const [getSourceOfIncomeDictionary] =
    useLazyGetSourceOfIncomeDictionaryQuery();
  const [getSourceOfWealthForIndividualDictionary] =
    useLazyGetSourceOfWealthForIndividualDictionaryQuery();
  const [
    getCrsAndFatca,
    { isLoading: crsAndFatcaIsLoading, isFetching: crsAndFatcaIsFetching },
  ] = useLazyGetCOFCRSAndFactaQuery();
  const [createCrsAndFatca, { isLoading: createCrsAndFatcaIsProgress }] =
    useCreateCOFCRSAndFactaMutation();
  const [updateCrsAndFatca, { isLoading: updateCrsAndFatcaInProgress }] =
    useUpdateCOFCRSAndFactaMutation();
  const [
    deleteCrsAndFatca,
    {
      isLoading: deleteCrsAndFatcaInProgress,
      isSuccess: deleteCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCOFCRSAndFactaMutation();
  const [
    getAffilatedCompanies,
    {
      isLoading: affilatedCompaniesIsLoading,
      isFetching: affilatedCompaniesIsFetching,
    },
  ] = useLazyGetAffilatedCompaniesQuery();
  const [
    createAffilatedCompany,
    { isLoading: createAffilatedCompanyInProgress },
  ] = useCreateAffilatedCompaniesMutation();
  const [
    updateAffilatedCompany,
    { isLoading: updateAffilatedCompanyInProgress },
  ] = useUpdateAffilatedCompaniesMutation();
  const [
    deleteAffilatedCompany,
    {
      isLoading: deleteAffilatedCompanyInProgress,
      isSuccess: deleteAffilatedCompanyIsSuccess,
    },
  ] = useDeleteAffilatedCompaniesMutation();

  const onSaveCRSAndFatca = async () => {
    const crsAndFatcaForCreation: CRSAndFatcaFormValues[] = [];
    const crsAndFatcaForForUpdate: CRSAndFatcaFormValues[] = [];

    crsAndFatcaValues?.forEach((item) => {
      if (item.id?.includes('crs-and-fatca')) {
        crsAndFatcaForCreation.push(item);
      } else {
        const currentcrsAndFatca = crsAndFatca?.find(
          (value) => value.id === item.id,
        );

        if (currentcrsAndFatca) {
          if (
            !isEqual(item, {
              id: currentcrsAndFatca.id,
              residenceCountryId: currentcrsAndFatca?.residenceCountryId,
              taxIdentificationNumber:
                currentcrsAndFatca?.taxIdentificationNumber,
              noTinReason: currentcrsAndFatca?.noTinReason,
            })
          ) {
            crsAndFatcaForForUpdate.push(item);
          }
        }
      }
    });

    if (crsAndFatcaForCreation?.length) {
      await Promise.all(
        crsAndFatcaForCreation
          ?.filter((item) => item.residenceCountryId)
          ?.map(async (item) => {
            const response = await createCrsAndFatca({
              id: questionnarieId as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });

            return response;
          }),
      );
    }

    if (crsAndFatcaForForUpdate?.length) {
      await Promise.all(
        crsAndFatcaForForUpdate?.map(async (item) => {
          const response = await updateCrsAndFatca({
            id: item.id as string,
            body: {
              residenceCountryId: item.residenceCountryId,
              taxIdentificationNumber: item.taxIdentificationNumber,
              noTinReason: item.noTinReason,
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteCRSAndFatca = async (id: string) => {
    if (id.includes('crs-and-fatca')) {
      setCrsAndFatcaValues(crsAndFatcaValues?.filter((item) => item.id !== id));
    } else {
      setSelectedCrsAndFatcaId(id);

      await deleteCrsAndFatca(id);
    }
  };

  const onSaveAffilatedCompanies = async () => {
    const affilatedCompaniesForCreate: AffilatedCompanyFormValues[] = [];
    const affilatedCompaniesForForUpdate: AffilatedCompanyFormValues[] = [];

    affilatedCompaniesValues?.forEach((item) => {
      if (item.id?.includes('affilated-company')) {
        affilatedCompaniesForCreate.push(item);
      } else {
        const currentAffilatedCompany = affilatedCompanies?.find(
          (value) => value.id === item.id,
        );

        if (currentAffilatedCompany) {
          if (
            !isEqual(item, {
              id: currentAffilatedCompany.id,
              name: currentAffilatedCompany.name,
              registrationNumber: currentAffilatedCompany.registrationNumber,
              mainActivities: currentAffilatedCompany.mainActivities,
              position: currentAffilatedCompany.position,
              countryId: currentAffilatedCompany.countryId,
              percentageOfOwnership:
                currentAffilatedCompany.percentageOfOwnership,
            })
          ) {
            affilatedCompaniesForForUpdate.push(item);
          }
        }
      }
    });

    if (affilatedCompaniesForCreate?.length) {
      await Promise.all(
        affilatedCompaniesForCreate
          ?.filter(
            (item) =>
              item.name &&
              item.registrationNumber &&
              item.mainActivities &&
              item.position &&
              item.countryId &&
              item.percentageOfOwnership,
          )
          ?.map(async (item) => {
            const response = await createAffilatedCompany({
              id: questionnarieId as string,
              body: {
                name: item.name,
                registrationNumber: item.registrationNumber,
                mainActivities: item.mainActivities,
                position: item.position,
                countryId: item.countryId,
                percentageOfOwnership: Number(item.percentageOfOwnership),
              },
            });

            return response;
          }),
      );
    }

    if (affilatedCompaniesForForUpdate?.length) {
      await Promise.all(
        affilatedCompaniesForForUpdate?.map(async (item) => {
          const response = await updateAffilatedCompany({
            id: item.id as string,
            body: {
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAffilatedCompany = async (id: string) => {
    if (id.includes('affilated-company')) {
      setAffilatedCompaniesValues(
        affilatedCompaniesValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedAffilatedCompanyId(id);

      await deleteAffilatedCompany(id);
    }
  };

  useEffect(() => {
    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (affilatedCompanies) {
      setAffilatedCompaniesValues(
        affilatedCompanies?.map((item) => ({
          id: item.id,
          name: item.name,
          registrationNumber: item.registrationNumber,
          mainActivities: item.mainActivities,
          position: item.position,
          countryId: item.countryId,
          percentageOfOwnership: item.percentageOfOwnership?.toString(),
        })),
      );
    }
  }, [affilatedCompanies]);

  useEffect(() => {
    if (deleteCrsAndFatcaIsSuccess) {
      setSelectedCrsAndFatcaId('');
    }
  }, [deleteCrsAndFatcaIsSuccess]);

  useEffect(() => {
    if (deleteAffilatedCompanyIsSuccess) {
      setSelectedAffilatedCompanyId('');
    }
  }, [deleteAffilatedCompanyIsSuccess]);

  useEffect(() => {
    if (newApplicationInfo && newApplicationInfo?.applicationScheme) {
      if (
        newApplicationInfo?.applicationScheme?.civilActive ||
        newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
        newApplicationInfo?.applicationScheme
          ?.lastThreeYearsAddressHistoryActive ||
        newApplicationInfo?.applicationScheme
          ?.nationalInsuranceNumberOfDirectorsActive ||
        newApplicationInfo?.applicationScheme?.regulatoryActive ||
        newApplicationInfo?.applicationScheme?.businessAndEmploymentActive ||
        newApplicationInfo?.hasQuestionnaire
      ) {
        if (!questionnarie) {
          getApplicationQuestionnarie(newApplicationInfo?.id);
        }
      }
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!sourceOfWealth) {
        getCustomerSourceOfWealth();
      }

      if (!sourceOfIncome) {
        getSourceOfIncomeDictionary();
      }

      if (!sourceOfWealthForIndividual) {
        getSourceOfWealthForIndividualDictionary();
      }
    }
  }, [
    sourceOfWealth,
    activePermissions,
    newApplicationInfo,
    sourceOfIncome,
    sourceOfWealthForIndividual,
  ]);

  useEffect(() => {
    if (
      questionnarieId &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      const nextAllowedCategories =
        newApplicationInfo?.applicationScheme?.documentsCategories?.edges?.map(
          (item) => item.node,
        ) as AllowedCategory[];

      nextAllowedCategories?.unshift({
        id: 'identification-category',
        name: 'Identification documents',
        required: false,
      });

      dispatch(setAllowedDocumentsCategories(nextAllowedCategories));

      if (!crsAndFatca) {
        getCrsAndFatca(questionnarieId);
      }

      if (!affilatedCompanies) {
        getAffilatedCompanies(questionnarieId);
      }
    }
  }, [questionnarieId, newApplicationInfo, crsAndFatca, affilatedCompanies]);

  useEffect(() => {
    if (questionnarie) {
      questionnarieReset(questionnarie);
      setDefaultQuestionnarieFormValues(questionnarie);
    }
  }, [questionnarie]);

  return {
    questionnarieFields,
    crsAndFatcaValues,
    haveSecondPassport,
    affilatedCompaniesValues,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveCRSAndFatca,
    onSaveAffilatedCompanies,
    questionnarieSetValue,
    defaultQuestionnarieFormValues,
    questionnarieIsFetching,
    questionnarieIsLoading,
    createCrsAndFatcaIsProgress,
    updateCrsAndFatcaInProgress,
    createAffilatedCompanyInProgress,
    updateAffilatedCompanyInProgress,
    questionnarieClearErrors,
    setHaveSecondPassport,
    questionnarieControl,
    questionnarieErrors,
    setAffilatedCompaniesValues,
    affilatedCompaniesIsLoading,
    affilatedCompaniesIsFetching,
    deleteAffilatedCompanyInProgress,
    onDeleteAffilatedCompany,
    selectedAffilatedCompanyId,
    crsAndFatcaIsLoading,
    crsAndFatcaIsFetching,
    deleteCrsAndFatcaInProgress,
    onDeleteCRSAndFatca,
    setCrsAndFatcaValues,
    selectedCrsAndFatcaId,
    questionnarieIsDirty,
  };
};

export default useQuestionnarie;
